import { jsx as _jsx } from "react/jsx-runtime";
import * as ActiveStorage from '@rails/activestorage';
import Rails from '@rails/ujs';
import Turbolinks from 'turbolinks';
import { createRoot } from 'react-dom/client';
import '../datadog/datadogRUM-init';
import { QueryClient } from '@tanstack/react-query';
export const queryClient = new QueryClient();
Rails.start();
Turbolinks.start();
ActiveStorage.start();
window.Turbolinks = Turbolinks;
/**
 * Dynamically importable React components context with lazy loading structures
 */ const componentContext = require.context('components', true, /\.jsx$/, 'lazy');
/**
 * Set of available component keys for quick lookup
 */ const componentKeys = new Set(componentContext.keys());
/**
 * Keep track of mounted React roots to avoid remounting and to unmount before caching
 */ const mountedRoots = new WeakMap();
const resolveComponentKey = (path)=>{
    const candidate = `./${path}.jsx`;
    if (componentKeys.has(candidate)) return candidate;
    console.error(`[CMS | application.js] Could not resolve component using the key for path: ${path} (looked for ${candidate})`);
    return undefined;
};
const safeParseProps = (raw, element)=>{
    if (!raw) return {};
    try {
        return JSON.parse(raw);
    } catch (error) {
        console.warn('Failed to parse data-react-props', error, element);
        return {};
    }
};
const mountReactComponents = async ()=>{
    const nodes = document.querySelectorAll('[data-react-component]');
    if (nodes.length === 0) return;
    const mountTargets = [];
    const componentsToLoad = new Map();
    for (const el of nodes){
        if (mountedRoots.has(el)) continue;
        const path = el.dataset.reactComponent;
        const key = resolveComponentKey(path);
        if (!key) continue;
        mountTargets.push({
            el,
            key,
            rawProps: el.dataset.reactProps
        });
        if (!componentsToLoad.has(key)) {
            componentsToLoad.set(key, componentContext(key));
        }
    }
    const loadedComponents = new Map();
    await Promise.all([
        ...componentsToLoad.entries()
    ].map(async ([key, promise])=>{
        try {
            const module = await promise;
            loadedComponents.set(key, module.default || module);
        } catch (e) {
            console.error(`Failed to load component: ${key}`, e);
        }
    }));
    for (const { el, key, rawProps } of mountTargets){
        const Component = loadedComponents.get(key);
        if (!Component) continue;
        const props = safeParseProps(rawProps, el);
        const root = createRoot(el);
        root.render(/*#__PURE__*/ _jsx(Component, {
            ...props
        }));
        mountedRoots.set(el, root);
    }
};
const unmountReactComponents = ()=>{
    const elements = document.querySelectorAll('[data-react-component]');
    elements.forEach((el)=>{
        const root = mountedRoots.get(el);
        if (root) {
            /**
       * Unmount the React component from the DOM node and clean up resources
       *
       * @see https://react.dev/reference/react-dom/client/createRoot
       */ root.unmount();
            mountedRoots.delete(el); // Remove reference from the map
        }
    });
};
// Transient DOM nodes Bootstrap appends outside React roots (backdrops, floats, etc.)
const ARTIFACT_SELECTORS = [
    '.modal-backdrop',
    '.offcanvas-backdrop',
    '.tooltip',
    '.popover'
];
// Classes Bootstrap adds to <body> that must be cleared on navigation
const BODY_CLASS_ARTIFACTS = [
    'modal-open'
];
// Inline styles Bootstrap sets on <body> that must be cleared on navigation
const BODY_STYLE_ARTIFACTS = [
    'overflow',
    'padding-right'
];
// Each entry describes an overlay type: how to detect it open and how to visually dismiss it.
// Turbolinks caches/restores <body>, so overlays left open across visits leave orphaned artifacts.
const OVERLAY_DISMISS = [
    {
        selector: '.modal.show',
        dismiss: (el)=>{
            el.classList.remove('show');
            el.style.display = 'none';
            el.setAttribute('aria-hidden', 'true');
            el.removeAttribute('aria-modal');
            el.removeAttribute('role');
        }
    },
    {
        selector: '.offcanvas.show',
        dismiss: (el)=>el.classList.remove('show', 'showing')
    }
];
const removePageArtifacts = ()=>{
    ARTIFACT_SELECTORS.forEach((sel)=>document.querySelectorAll(sel).forEach((el)=>el.remove()));
    BODY_CLASS_ARTIFACTS.forEach((cls)=>document.body.classList.remove(cls));
    BODY_STYLE_ARTIFACTS.forEach((prop)=>document.body.style.removeProperty(prop));
};
const dismissOpenOverlays = ()=>{
    OVERLAY_DISMISS.forEach(({ selector, dismiss })=>{
        document.querySelectorAll(selector).forEach(dismiss);
    });
    removePageArtifacts();
};
document.addEventListener('turbolinks:load', ()=>{
    // Artifacts present at load time were restored from a stale cached snapshot — heal before mounting React.
    const hasOpenOverlay = OVERLAY_DISMISS.some(({ selector })=>document.querySelector(selector));
    if (!hasOpenOverlay) removePageArtifacts();
    mountReactComponents().catch((err)=>{
        console.error('Error mounting React components on turbolinks:load', err);
    });
});
document.addEventListener('turbolinks:before-cache', ()=>{
    dismissOpenOverlays();
    unmountReactComponents();
});
/**
 * Bootstrap theme related JS and other non-React code
 */ import '../theme/theme';
import '../src/dynamic_add';
import '../src/dynamic_hidden_fields';
import '../src/google_places_autocomplete';
import '../src/site_attributes_builder';
import '../src/localisedTime';
import '../src/instant_modal';
import '../src/confirmation_dialog';
import '../src/sidebar';
import '../src/job_url_builder';
import '../src/password_strength_meter';
import '../src/column_picker';
